import { useState, useEffect, useMemo } from 'react';
import { ativosService, ativoCategoriasService } from '../../services';
import { formatCurrency, formatDate } from '../../utils/format';

const FORM_VAZIO = {
  nome: '', categoria: '', quantidade: '1', valor_unitario: '', data_aquisicao: '',
  estado: 'bom', localizacao: '', observacoes: '', status: 'ativo',
};

const ESTADO_LABEL = { novo: '🟢 Novo', bom: '🔵 Bom', regular: '🟡 Regular', ruim: '🔴 Ruim' };

const formatTamanho = (bytes) => {
  const n = Number(bytes || 0);
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

export default function AtivosFixos() {
  const [ativos, setAtivos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [ordenar, setOrdenar] = useState({ campo: 'nome', dir: 'asc' });
  const [modal, setModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(FORM_VAZIO);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  // Visualização (somente leitura) + anexos
  const [verAtivo, setVerAtivo] = useState(null);
  const [anexos, setAnexos] = useState([]);
  const [anexosLoading, setAnexosLoading] = useState(false);
  const [enviandoAnexo, setEnviandoAnexo] = useState(false);
  const [anexosPendentes, setAnexosPendentes] = useState([]); // arquivos escolhidos antes de o ativo existir
  // Gestão de categorias
  const [categoriasGerenciadas, setCategoriasGerenciadas] = useState([]);
  const [modalCategorias, setModalCategorias] = useState(false);
  const [novaCategoria, setNovaCategoria] = useState('');
  const [editCategoria, setEditCategoria] = useState(null); // { id, nome }
  const [erroCategoria, setErroCategoria] = useState('');

  const carregar = () => {
    setLoading(true);
    ativosService.listar()
      .then(r => setAtivos(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const carregarCategorias = () => {
    ativoCategoriasService.listar()
      .then(r => setCategoriasGerenciadas(r.data))
      .catch(() => {});
  };

  useEffect(() => { carregar(); carregarCategorias(); }, []);

  const carregarAnexos = (ativoId) => {
    setAnexosLoading(true);
    ativosService.listarAnexos(ativoId)
      .then(r => setAnexos(r.data))
      .catch(() => setAnexos([]))
      .finally(() => setAnexosLoading(false));
  };

  const abrirNovo = () => {
    setEditando(null);
    setForm(FORM_VAZIO);
    setAnexos([]);
    setAnexosPendentes([]);
    setErro('');
    setModal(true);
  };

  const abrirEditar = (a) => {
    setEditando(a.id);
    setForm({
      nome: a.nome || '',
      categoria: a.categoria || '',
      quantidade: String(a.quantidade ?? '1'),
      valor_unitario: a.valor_unitario != null ? String(a.valor_unitario) : '',
      data_aquisicao: a.data_aquisicao ? a.data_aquisicao.slice(0, 10) : '',
      estado: a.estado || 'bom',
      localizacao: a.localizacao || '',
      observacoes: a.observacoes || '',
      status: a.status || 'ativo',
    });
    setAnexosPendentes([]);
    setErro('');
    setModal(true);
    carregarAnexos(a.id);
  };

  const abrirVer = (a) => {
    setVerAtivo(a);
    setAnexos([]);
    carregarAnexos(a.id);
  };

  const salvar = async () => {
    if (!form.nome.trim()) { setErro('Nome é obrigatório'); return; }
    setSalvando(true);
    setErro('');
    try {
      if (editando) {
        await ativosService.atualizar(editando, form);
      } else {
        const r = await ativosService.criar(form);
        const novoId = r.data?.id;
        // Envia os anexos escolhidos durante o cadastro do novo ativo
        if (novoId && anexosPendentes.length) {
          for (const file of anexosPendentes) {
            const fd = new FormData();
            fd.append('arquivo', file);
            await ativosService.enviarAnexo(novoId, fd);
          }
        }
      }
      setModal(false);
      carregar();
    } catch (err) {
      setErro(err.response?.data?.error || 'Erro ao salvar');
    } finally {
      setSalvando(false);
    }
  };

  const remover = async (a) => {
    if (!window.confirm(`Remover o ativo "${a.nome}"? Esta ação é irreversível.`)) return;
    try {
      await ativosService.remover(a.id);
      carregar();
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao remover');
    }
  };

  // Envia anexo para um ativo que já existe (modal de edição ou de visualização)
  const enviarAnexoArquivo = async (ativoId, file) => {
    if (!file) return;
    setEnviandoAnexo(true);
    try {
      const fd = new FormData();
      fd.append('arquivo', file);
      await ativosService.enviarAnexo(ativoId, fd);
      carregarAnexos(ativoId);
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao enviar anexo');
    } finally {
      setEnviandoAnexo(false);
    }
  };

  const removerAnexo = async (ativoId, anexo) => {
    if (!window.confirm(`Remover o anexo "${anexo.nome_original}"?`)) return;
    try {
      await ativosService.removerAnexo(anexo.id);
      carregarAnexos(ativoId);
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao remover anexo');
    }
  };

  const abrirAnexo = async (anexo) => {
    try {
      const r = await ativosService.baixarAnexo(anexo.id);
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      alert('Erro ao abrir o anexo');
    }
  };

  const categorias = useMemo(
    () => [...new Set(ativos.map(a => a.categoria).filter(Boolean))].sort(),
    [ativos]
  );

  // Lista para o <select> do cadastro: categorias gerenciadas + a atual do ativo (se ainda não estiver na lista)
  const categoriasSelect = useMemo(() => {
    const nomes = new Set(categoriasGerenciadas.map(c => c.nome));
    if (form.categoria) nomes.add(form.categoria);
    return [...nomes].sort();
  }, [categoriasGerenciadas, form.categoria]);

  const criarCategoria = async () => {
    if (!novaCategoria.trim()) return;
    setErroCategoria('');
    try {
      await ativoCategoriasService.criar({ nome: novaCategoria.trim() });
      setNovaCategoria('');
      carregarCategorias();
    } catch (err) {
      setErroCategoria(err.response?.data?.error || 'Erro ao criar categoria');
    }
  };

  const salvarEdicaoCategoria = async () => {
    if (!editCategoria?.nome.trim()) return;
    setErroCategoria('');
    try {
      await ativoCategoriasService.atualizar(editCategoria.id, { nome: editCategoria.nome.trim() });
      setEditCategoria(null);
      carregarCategorias();
      carregar();
    } catch (err) {
      setErroCategoria(err.response?.data?.error || 'Erro ao atualizar categoria');
    }
  };

  const removerCategoria = async (cat) => {
    if (!window.confirm(`Remover a categoria "${cat.nome}"?`)) return;
    setErroCategoria('');
    try {
      await ativoCategoriasService.remover(cat.id);
      carregarCategorias();
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao remover categoria');
    }
  };

  const alternarOrdenacao = (campo) => {
    setOrdenar(o => o.campo === campo
      ? { campo, dir: o.dir === 'asc' ? 'desc' : 'asc' }
      : { campo, dir: 'asc' });
  };

  const setaOrdenacao = (campo) => ordenar.campo === campo ? (ordenar.dir === 'asc' ? ' ▲' : ' ▼') : '';

  const ativosFiltrados = useMemo(() => {
    const b = busca.trim().toLowerCase();
    const filtrados = ativos.filter(a => {
      const matchBusca = !b
        || a.nome.toLowerCase().includes(b)
        || (a.categoria || '').toLowerCase().includes(b)
        || (a.localizacao || '').toLowerCase().includes(b)
        || (a.observacoes || '').toLowerCase().includes(b);
      const matchCategoria = !filtroCategoria || a.categoria === filtroCategoria;
      const matchStatus = !filtroStatus || a.status === filtroStatus;
      return matchBusca && matchCategoria && matchStatus;
    });

    const { campo, dir } = ordenar;
    const fator = dir === 'asc' ? 1 : -1;
    const valor = (a) => {
      switch (campo) {
        case 'total': return Number(a.valor_unitario || 0) * Number(a.quantidade || 0);
        case 'valor_unitario': return Number(a.valor_unitario || 0);
        case 'quantidade': return Number(a.quantidade || 0);
        case 'data_aquisicao': return a.data_aquisicao ? new Date(a.data_aquisicao).getTime() : 0;
        default: return (a[campo] || '').toString().toLowerCase();
      }
    };
    return [...filtrados].sort((x, y) => {
      const vx = valor(x), vy = valor(y);
      if (vx < vy) return -1 * fator;
      if (vx > vy) return 1 * fator;
      return 0;
    });
  }, [ativos, busca, filtroCategoria, filtroStatus, ordenar]);

  const totais = useMemo(() => {
    const ativosAtivos = ativos.filter(a => a.status === 'ativo');
    const valorTotal = ativosAtivos.reduce(
      (s, a) => s + Number(a.valor_unitario || 0) * Number(a.quantidade || 0), 0
    );
    const qtdItens = ativosAtivos.reduce((s, a) => s + Number(a.quantidade || 0), 0);
    return { valorTotal, qtdItens, totalRegistros: ativosAtivos.length };
  }, [ativos]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <h1>🏷️ Ativo Fixo (Patrimônio)</h1>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-outline" onClick={() => { setErroCategoria(''); setNovaCategoria(''); setEditCategoria(null); setModalCategorias(true); }}>⚙ Categorias</button>
          <button className="btn btn-primary" onClick={abrirNovo}>+ Novo Ativo</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
        <div className="card" style={{ padding: '12px 18px' }}>
          <div style={{ fontSize: 12, color: '#888' }}>Valor total (ativos)</div>
          <strong style={{ fontSize: 20, color: 'var(--primary)' }}>{formatCurrency(totais.valorTotal)}</strong>
        </div>
        <div className="card" style={{ padding: '12px 18px' }}>
          <div style={{ fontSize: 12, color: '#888' }}>Itens (unidades)</div>
          <strong style={{ fontSize: 20 }}>{totais.qtdItens}</strong>
        </div>
        <div className="card" style={{ padding: '12px 18px' }}>
          <div style={{ fontSize: 12, color: '#888' }}>Registros ativos</div>
          <strong style={{ fontSize: 20 }}>{totais.totalRegistros}</strong>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          type="text"
          className="form-control"
          placeholder="🔍 Buscar por nome, categoria, local ou observação..."
          value={busca}
          onChange={e => setBusca(e.target.value)}
          style={{ flex: 1, minWidth: 200 }}
        />
        <select className="form-control" value={filtroCategoria} onChange={e => setFiltroCategoria(e.target.value)} style={{ width: 'auto' }}>
          <option value="">Todas as categorias</option>
          {categorias.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select className="form-control" value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)} style={{ width: 'auto' }}>
          <option value="">Todos os status</option>
          <option value="ativo">Ativo</option>
          <option value="baixado">Baixado</option>
        </select>
        {(busca || filtroCategoria || filtroStatus) && (
          <button className="btn btn-outline" onClick={() => { setBusca(''); setFiltroCategoria(''); setFiltroStatus(''); }}>
            Limpar
          </button>
        )}
      </div>

      <div className="table-container card">
        {loading ? (
          <div className="loading"><div className="spinner"></div>Carregando...</div>
        ) : (
          <table>
            <thead>
              <tr>
                {[
                  ['nome', 'Ativo'],
                  ['categoria', 'Categoria'],
                  ['localizacao', 'Local'],
                  ['quantidade', 'Qtd'],
                  ['valor_unitario', 'Valor unit.'],
                  ['total', 'Total'],
                  ['estado', 'Estado'],
                  ['data_aquisicao', 'Aquisição'],
                  ['status', 'Status'],
                ].map(([campo, label]) => (
                  <th
                    key={campo}
                    onClick={() => alternarOrdenacao(campo)}
                    style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                    title="Clique para classificar"
                  >
                    {label}{setaOrdenacao(campo)}
                  </th>
                ))}
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {ativosFiltrados.map(a => (
                <tr key={a.id} style={a.status === 'baixado' ? { opacity: 0.55 } : undefined}>
                  <td><strong>{a.nome}</strong>{a.total_anexos > 0 ? ` 📎${a.total_anexos}` : ''}</td>
                  <td>{a.categoria || '—'}</td>
                  <td>{a.localizacao || '—'}</td>
                  <td>{a.quantidade}</td>
                  <td>{formatCurrency(a.valor_unitario)}</td>
                  <td><strong>{formatCurrency(Number(a.valor_unitario || 0) * Number(a.quantidade || 0))}</strong></td>
                  <td>{ESTADO_LABEL[a.estado] || a.estado}</td>
                  <td>{a.data_aquisicao ? formatDate(a.data_aquisicao) : '—'}</td>
                  <td>
                    <span className={`badge ${a.status === 'ativo' ? 'badge-success' : 'badge-danger'}`}>
                      {a.status === 'ativo' ? 'Ativo' : 'Baixado'}
                    </span>
                  </td>
                  <td style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-sm btn-outline" onClick={() => abrirVer(a)}>Ver</button>
                    <button className="btn btn-sm btn-primary" onClick={() => abrirEditar(a)}>Editar</button>
                    <button className="btn btn-sm btn-danger" onClick={() => remover(a)}>Remover</button>
                  </td>
                </tr>
              ))}
              {ativosFiltrados.length === 0 && (
                <tr><td colSpan="10" className="no-data">
                  {ativos.length === 0 ? 'Nenhum ativo cadastrado' : 'Nenhum ativo encontrado'}
                </td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {modal && (
        <div className="modal-overlay" onClick={() => !salvando && setModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <h2>{editando ? 'Editar Ativo' : 'Novo Ativo'}</h2>

            <div className="form-group">
              <label>Nome *</label>
              <input
                type="text"
                className="form-control"
                value={form.nome}
                onChange={e => setForm({ ...form, nome: e.target.value })}
                placeholder="Ex: Chapa a gás, Freezer, Mesa, Cadeira..."
                autoFocus
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Categoria</span>
                  <button
                    type="button"
                    onClick={() => { setErroCategoria(''); setNovaCategoria(''); setEditCategoria(null); setModalCategorias(true); }}
                    style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: 12, padding: 0 }}
                  >
                    ⚙ Gerenciar
                  </button>
                </label>
                <select
                  className="form-control"
                  value={form.categoria}
                  onChange={e => setForm({ ...form, categoria: e.target.value })}
                >
                  <option value="">Sem categoria</option>
                  {categoriasSelect.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Localização</label>
                <input
                  type="text"
                  className="form-control"
                  value={form.localizacao}
                  onChange={e => setForm({ ...form, localizacao: e.target.value })}
                  placeholder="Ex: Cozinha, Salão"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Quantidade</label>
                <input
                  type="number" min="1" step="1"
                  className="form-control"
                  value={form.quantidade}
                  onChange={e => setForm({ ...form, quantidade: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Valor unitário (R$)</label>
                <input
                  type="number" min="0" step="0.01"
                  className="form-control"
                  value={form.valor_unitario}
                  onChange={e => setForm({ ...form, valor_unitario: e.target.value })}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Data de aquisição</label>
                <input
                  type="date"
                  className="form-control"
                  value={form.data_aquisicao}
                  onChange={e => setForm({ ...form, data_aquisicao: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Estado de conservação</label>
                <select className="form-control" value={form.estado} onChange={e => setForm({ ...form, estado: e.target.value })}>
                  <option value="novo">Novo</option>
                  <option value="bom">Bom</option>
                  <option value="regular">Regular</option>
                  <option value="ruim">Ruim</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Status</label>
              <select className="form-control" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                <option value="ativo">Ativo (em uso)</option>
                <option value="baixado">Baixado (descartado / vendido)</option>
              </select>
            </div>

            <div className="form-group">
              <label>Observações</label>
              <textarea
                className="form-control"
                rows={2}
                value={form.observacoes}
                onChange={e => setForm({ ...form, observacoes: e.target.value })}
                placeholder="Número de série, nota fiscal, garantia, etc."
              />
            </div>

            <div className="form-group">
              <label>Anexos <span style={{ fontWeight: 'normal', color: '#888', fontSize: 12 }}>(nota fiscal, foto, garantia, manual — até 10 MB)</span></label>

              {editando ? (
                <>
                  {anexosLoading ? (
                    <p style={{ color: '#888', fontSize: 13 }}>Carregando anexos...</p>
                  ) : anexos.length === 0 ? (
                    <p style={{ color: '#888', fontSize: 13 }}>Nenhum anexo.</p>
                  ) : (
                    <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 8px' }}>
                      {anexos.map(an => (
                        <li key={an.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', borderBottom: '1px solid #eee' }}>
                          <span style={{ flex: 1, wordBreak: 'break-all' }}>📎 {an.nome_original} <small style={{ color: '#999' }}>({formatTamanho(an.tamanho)})</small></span>
                          <button type="button" className="btn btn-sm btn-outline" onClick={() => abrirAnexo(an)}>Abrir</button>
                          <button type="button" className="btn btn-sm btn-danger" onClick={() => removerAnexo(editando, an)}>Excluir</button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <input
                    type="file"
                    disabled={enviandoAnexo}
                    onChange={e => { const f = e.target.files[0]; if (f) enviarAnexoArquivo(editando, f); e.target.value = ''; }}
                  />
                  {enviandoAnexo && <span style={{ marginLeft: 8, color: '#888', fontSize: 13 }}>Enviando...</span>}
                </>
              ) : (
                <>
                  {anexosPendentes.length > 0 && (
                    <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 8px' }}>
                      {anexosPendentes.map((f, i) => (
                        <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', borderBottom: '1px solid #eee' }}>
                          <span style={{ flex: 1, wordBreak: 'break-all' }}>📎 {f.name} <small style={{ color: '#999' }}>({formatTamanho(f.size)})</small></span>
                          <button type="button" className="btn btn-sm btn-danger" onClick={() => setAnexosPendentes(prev => prev.filter((_, j) => j !== i))}>Remover</button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <input
                    type="file"
                    onChange={e => { const f = e.target.files[0]; if (f) setAnexosPendentes(prev => [...prev, f]); e.target.value = ''; }}
                  />
                  <div style={{ color: '#888', fontSize: 12, marginTop: 4 }}>Os anexos serão enviados ao salvar o ativo.</div>
                </>
              )}
            </div>

            {erro && <p style={{ color: 'var(--danger)', marginBottom: 12 }}>{erro}</p>}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-outline" onClick={() => setModal(false)} disabled={salvando}>Cancelar</button>
              <button className="btn btn-primary" onClick={salvar} disabled={salvando}>
                {salvando ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {verAtivo && (
        <div className="modal-overlay" onClick={() => setVerAtivo(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
              <h2 style={{ margin: 0 }}>{verAtivo.nome}</h2>
              <span className={`badge ${verAtivo.status === 'ativo' ? 'badge-success' : 'badge-danger'}`}>
                {verAtivo.status === 'ativo' ? 'Ativo' : 'Baixado'}
              </span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px', margin: '16px 0' }}>
              <div><small style={{ color: '#888' }}>Categoria</small><br />{verAtivo.categoria || '—'}</div>
              <div><small style={{ color: '#888' }}>Localização</small><br />{verAtivo.localizacao || '—'}</div>
              <div><small style={{ color: '#888' }}>Quantidade</small><br />{verAtivo.quantidade}</div>
              <div><small style={{ color: '#888' }}>Valor unitário</small><br />{formatCurrency(verAtivo.valor_unitario)}</div>
              <div><small style={{ color: '#888' }}>Valor total</small><br /><strong>{formatCurrency(Number(verAtivo.valor_unitario || 0) * Number(verAtivo.quantidade || 0))}</strong></div>
              <div><small style={{ color: '#888' }}>Estado</small><br />{ESTADO_LABEL[verAtivo.estado] || verAtivo.estado}</div>
              <div><small style={{ color: '#888' }}>Aquisição</small><br />{verAtivo.data_aquisicao ? formatDate(verAtivo.data_aquisicao) : '—'}</div>
              {verAtivo.status === 'baixado' && (
                <div><small style={{ color: '#888' }}>Data da baixa</small><br />{verAtivo.data_baixa ? formatDate(verAtivo.data_baixa) : '—'}</div>
              )}
            </div>

            {verAtivo.observacoes && (
              <div style={{ marginBottom: 16 }}>
                <small style={{ color: '#888' }}>Observações</small>
                <div style={{ whiteSpace: 'pre-wrap' }}>{verAtivo.observacoes}</div>
              </div>
            )}

            <div className="form-group">
              <label>Anexos</label>
              {anexosLoading ? (
                <p style={{ color: '#888', fontSize: 13 }}>Carregando anexos...</p>
              ) : anexos.length === 0 ? (
                <p style={{ color: '#888', fontSize: 13 }}>Nenhum anexo.</p>
              ) : (
                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 8px' }}>
                  {anexos.map(an => (
                    <li key={an.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', borderBottom: '1px solid #eee' }}>
                      <span style={{ flex: 1, wordBreak: 'break-all' }}>📎 {an.nome_original} <small style={{ color: '#999' }}>({formatTamanho(an.tamanho)})</small></span>
                      <button type="button" className="btn btn-sm btn-primary" onClick={() => abrirAnexo(an)}>Abrir</button>
                      <button type="button" className="btn btn-sm btn-danger" onClick={() => removerAnexo(verAtivo.id, an)}>Excluir</button>
                    </li>
                  ))}
                </ul>
              )}
              <input
                type="file"
                disabled={enviandoAnexo}
                onChange={e => { const f = e.target.files[0]; if (f) enviarAnexoArquivo(verAtivo.id, f); e.target.value = ''; }}
              />
              {enviandoAnexo && <span style={{ marginLeft: 8, color: '#888', fontSize: 13 }}>Enviando...</span>}
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
              <button className="btn btn-primary" onClick={() => { const a = verAtivo; setVerAtivo(null); abrirEditar(a); }}>Editar</button>
              <button className="btn btn-outline" onClick={() => setVerAtivo(null)}>Fechar</button>
            </div>
          </div>
        </div>
      )}

      {modalCategorias && (
        <div className="modal-overlay" onClick={() => setModalCategorias(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
            <h2>Gerenciar Categorias</h2>

            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <input
                type="text"
                className="form-control"
                placeholder="Nova categoria..."
                value={novaCategoria}
                onChange={e => setNovaCategoria(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && criarCategoria()}
                style={{ flex: 1 }}
              />
              <button className="btn btn-primary" onClick={criarCategoria}>Adicionar</button>
            </div>

            {erroCategoria && <p style={{ color: 'var(--danger)', marginBottom: 12 }}>{erroCategoria}</p>}

            {categoriasGerenciadas.length === 0 ? (
              <p style={{ color: '#888' }}>Nenhuma categoria cadastrada.</p>
            ) : (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {categoriasGerenciadas.map(cat => (
                  <li key={cat.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid #eee' }}>
                    {editCategoria?.id === cat.id ? (
                      <>
                        <input
                          type="text"
                          className="form-control"
                          value={editCategoria.nome}
                          onChange={e => setEditCategoria({ ...editCategoria, nome: e.target.value })}
                          onKeyDown={e => e.key === 'Enter' && salvarEdicaoCategoria()}
                          style={{ flex: 1 }}
                          autoFocus
                        />
                        <button className="btn btn-sm btn-primary" onClick={salvarEdicaoCategoria}>Salvar</button>
                        <button className="btn btn-sm btn-outline" onClick={() => setEditCategoria(null)}>Cancelar</button>
                      </>
                    ) : (
                      <>
                        <span style={{ flex: 1 }}>{cat.nome} <small style={{ color: '#999' }}>({cat.total_ativos})</small></span>
                        <button className="btn btn-sm btn-outline" onClick={() => { setErroCategoria(''); setEditCategoria({ id: cat.id, nome: cat.nome }); }}>Editar</button>
                        <button className="btn btn-sm btn-danger" onClick={() => removerCategoria(cat)}>Remover</button>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="btn btn-outline" onClick={() => setModalCategorias(false)}>Fechar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
